import clsx from 'clsx';
import React, { Fragment, useCallback } from 'react';

import useWindowSize from '@hooks/useWindowSize';
import { OfferOption } from '@utils/backend-api/subscribe';
import { SelectedExtendedOffer } from '@utils/backend-api/subscribe';
import { WIDTH_MD, WIDTH_SM } from '@utils/constants';

import Offer from './Offer';
import SignupForm from './SignupForm';

interface MultipleOffersProps {
  offers: OfferOption[];
  onFormSubmit?: () => void;
  selectedOffer: SelectedExtendedOffer;
  setSelectedOffer: (plan: SelectedExtendedOffer) => void;
}

const MultipleOffers = ({
  offers,
  onFormSubmit,
  selectedOffer,
  setSelectedOffer,
}: MultipleOffersProps) => {
  const { width: windowWidth } = useWindowSize();

  const showSignupFormAndBorder = useCallback(
    (offer, index) => {
      const selectedOfferIndex = offers.findIndex((offer) => offer.code === selectedOffer?.code);
      const isLastOfferOfOddOffersLength = offers.length % 2 === 1 && offers.length - 1 === index;
      // This is the index of the signup form where it should be inserted on tablet size
      const signupIndex =
        selectedOfferIndex % 2 === 1 || isLastOfferOfOddOffersLength
          ? selectedOfferIndex
          : selectedOfferIndex + 1;
      const showSignupFormOnMobile = windowWidth < WIDTH_SM && selectedOffer?.code === offer.code;
      const showSignupFormOnTablet =
        signupIndex === index && windowWidth >= WIDTH_SM && windowWidth < WIDTH_MD;

      const showForm = selectedOffer && (showSignupFormOnMobile || showSignupFormOnTablet);
      // Show a bottom border under the signup form as long as there are more offers below it on tablet size
      const showBorder = offers.length - 2 >= signupIndex;

      return [showForm, showBorder];
    },
    [selectedOffer, offers, windowWidth]
  );

  return (
    <div
      className={clsx('grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-32', {
        'md:grid-cols-4 md:gap-8': offers.length > 3,
        'md:grid-cols-3 md:gap-28 md:px-96': offers.length === 3,
        'md:grid-cols-2 md:gap-32 md:px-[180px]': offers.length === 2,
      })}
    >
      {offers.map((offer, index) => {
        const [showSignupForm, showBorder] = showSignupFormAndBorder(offer, index);

        return (
          <Fragment key={`offer-card-${index}`}>
            <div className="h-full px-16 sm:px-0">
              <Offer
                offer={offer}
                selectedOfferCode={selectedOffer?.code}
                setSelectedOffer={setSelectedOffer}
              />
            </div>
            {showSignupForm && (
              <div
                className={clsx('px-16 sm:col-span-2 sm:px-0', {
                  'sm:border-0 sm:border-b sm:border-solid sm:border-gray-400 sm:px-0 md:border-none':
                    showBorder,
                })}
              >
                <SignupForm
                  formLocation={'subscribe_h'}
                  presentedOfferCodes={offers.map((offer) => offer.code)}
                  onFormSubmit={onFormSubmit}
                  selectedOffer={selectedOffer}
                />
              </div>
            )}
            <div className="mb-48 mt-48 h-px bg-gray-400 last:hidden sm:m-0 sm:hidden"></div>
          </Fragment>
        );
      })}
    </div>
  );
};

export default MultipleOffers;
